// Modal used to stage a new row in CramListEditor.
//
// Note: this modal does **not** write to Firestore anymore. It returns a
// DraftCramItem to the editor; the editor adds it to its local buffer
// alongside other pending changes and the actual write happens when the
// user clicks "Save changes" at the bottom of the list.
import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Group,
    Modal,
    NumberInput,
    Select,
    Stack,
    Text,
    Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import { CramListMeta } from "../cramListsRegistry";

export interface DraftCramItem {
    id: string;
    labelEn: string;
    labelAr: string;
    score?: number;
    sectorKey?: string;
}

interface Props {
    opened: boolean;
    listMeta: CramListMeta;
    existingIds: Set<string>;
    sectorOptions?: { value: string; label: string }[];
    onClose: () => void;
    onAdd: (item: DraftCramItem) => boolean | void;
}

// Mirrors slugify() in utils/seedCRAMLists.js / shared-helpers/src/cramHelper.ts.
// Doc IDs MUST come out identical to the seeded shape so the engine's
// lookups continue to work.
function slugify(value: string): string {
    return String(value || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 120);
}

const CramAddItemModal: React.FC<Props> = ({
    opened,
    listMeta,
    existingIds,
    sectorOptions,
    onClose,
    onAdd,
}) => {
    const [labelEn, setLabelEn] = useState("");
    const [labelAr, setLabelAr] = useState("");
    const [score, setScore] = useState<number | string>(2);
    const [sectorKey, setSectorKey] = useState<string | null>(null);

    useEffect(() => {
        if (!opened) {
            setLabelEn("");
            setLabelAr("");
            setScore(2);
            setSectorKey(null);
        }
    }, [opened]);

    const isNoScore = listMeta.mode === "no_score";
    const showSectorPicker = !!listMeta.sectorRefListId;

    // Industry doc IDs must be unique across sectors. When a sector is
    // picked we prefix the slug with the sector key — matching the seed
    // semantics (slugify(`${sector}__${labelEn}`)).
    const proposedKey = useMemo(() => {
        const base = slugify(labelEn);
        if (!base) return "";
        if (showSectorPicker && sectorKey) {
            return slugify(`${sectorKey}__${labelEn}`);
        }
        return base;
    }, [labelEn, showSectorPicker, sectorKey]);

    const collision = proposedKey && existingIds.has(proposedKey);

    const canSubmit =
        labelEn.trim().length > 0 &&
        !!proposedKey &&
        !collision &&
        (isNoScore || Number.isFinite(Number(score))) &&
        (!showSectorPicker || !!sectorKey);

    const handleSubmit = () => {
        if (!canSubmit) {
            if (showSectorPicker && !sectorKey) {
                notifications.show({
                    title: "Sector required",
                    message: "Pick a sector for this industry.",
                    color: "red",
                    icon: <IconAlertCircle size={16} />,
                });
            }
            return;
        }
        const draft: DraftCramItem = {
            id: proposedKey,
            labelEn: labelEn.trim(),
            labelAr: labelAr.trim(),
        };
        if (!isNoScore) draft.score = Number(score);
        if (showSectorPicker && sectorKey) draft.sectorKey = sectorKey;
        const result = onAdd(draft);
        if (result !== false) {
            onClose();
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={`Add to ${listMeta.labelEn}`}
            size="md"
            centered
        >
            <Stack gap="sm">
                {showSectorPicker && (
                    <Select
                        label="Sector"
                        placeholder="Pick a sector"
                        data={sectorOptions ?? []}
                        value={sectorKey}
                        onChange={setSectorKey}
                        searchable
                        required
                        nothingFoundMessage="No sectors found"
                        description="Sectors are managed in the Sectors list. Add one there first if it's missing."
                    />
                )}
                <Textarea
                    label="Label (English)"
                    placeholder="e.g. Software Engineering"
                    value={labelEn}
                    onChange={(e) => setLabelEn(e.currentTarget.value)}
                    required
                    autosize
                    minRows={1}
                    maxRows={4}
                    error={
                        collision
                            ? `An item with key "${proposedKey}" already exists.`
                            : undefined
                    }
                />
                {listMeta.showArabic && (
                    <Textarea
                        label="Label (Arabic)"
                        placeholder="(optional)"
                        value={labelAr}
                        onChange={(e) => setLabelAr(e.currentTarget.value)}
                        dir="rtl"
                        autosize
                        minRows={1}
                        maxRows={4}
                    />
                )}
                {!isNoScore && (
                    <NumberInput
                        label="Score"
                        description="0–8 (matches the Excel convention)"
                        min={0}
                        max={8}
                        step={0.5}
                        clampBehavior="strict"
                        value={score}
                        onChange={setScore}
                        required
                    />
                )}
                <Text size="xs" c="dimmed">
                    Doc ID: <Text span fw={600}>{proposedKey || "—"}</Text>
                </Text>
                <Group justify="flex-end" mt="xs">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={!canSubmit}>
                        Add to draft
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default CramAddItemModal;
